import React, { useState, useEffect, useMemo } from "react";
import { useDataProvider, useNotify, useRefresh, useTranslate } from "react-admin";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";

interface TemplateData {
  notification?: string;
  shareArtist?: string;
  shareRadio?: string;
  shareShow?: string;
}

interface ValidationErrors {
  notification?: string;
  shareArtist?: string;
  shareRadio?: string;
  shareShow?: string;
}

const MessageTemplatesPage: React.FC = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<TemplateData>({
    notification: "",
    shareArtist: "",
    shareRadio: "",
    shareShow: "",
  });
  const [initialTemplates, setInitialTemplates] = useState<TemplateData>({
    notification: "",
    shareArtist: "",
    shareRadio: "",
    shareShow: "",
  });

  // Count ${} placeholders in a string
  const countPlaceholders = (text: string): number => {
    const matches = text.match(/\$\{\}/g);
    return matches ? matches.length : 0;
  };

  // Validate templates
  const validationErrors = useMemo<ValidationErrors>(() => {
    const errors: ValidationErrors = {};

    if (templates.notification && countPlaceholders(templates.notification) < 1) {
      errors.notification = translate('messageTemplatesPage.notificationError');
    }

    if (templates.shareArtist && countPlaceholders(templates.shareArtist) < 2) {
      errors.shareArtist = translate('messageTemplatesPage.shareArtistError');
    }

    if (templates.shareRadio && countPlaceholders(templates.shareRadio) < 2) {
      errors.shareRadio = translate('messageTemplatesPage.shareRadioError');
    }

    if (templates.shareShow && countPlaceholders(templates.shareShow) < 2) {
      errors.shareShow = translate('messageTemplatesPage.shareShowError');
    }

    return errors;
  }, [templates, translate]);

  const hasValidationErrors = Object.keys(validationErrors).length > 0;
  const isDirty = JSON.stringify(templates) !== JSON.stringify(initialTemplates);

  // Load all templates
  useEffect(() => {
    const loadTemplates = async () => {
      setLoading(true);
      try {
        // Fetch the single messageTemplates document from sync/messageTemplates
        const result = await dataProvider.getOne("sync", {
          id: "messageTemplates"
        });

        const data: TemplateData = {
          notification: result.data?.notification || "",
          shareArtist: result.data?.shareArtist || "",
          shareRadio: result.data?.shareRadio || "",
          shareShow: result.data?.shareShow || "",
        };

        setTemplates(data);
        setInitialTemplates(data);
      } catch (error: any) {
        // If document doesn't exist, initialize with empty strings
        if (error.message?.includes("Document not found")) {
          const emptyData: TemplateData = {
            notification: "",
            shareArtist: "",
            shareRadio: "",
            shareShow: "",
          };
          setTemplates(emptyData);
          setInitialTemplates(emptyData);
        } else {
          notify(`${translate('messageTemplatesPage.loadError')}: ${error.message}`, { type: "error" });
        }
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, [dataProvider, notify, translate]);

  const handleChange = (key: keyof TemplateData, value: string) => {
    setTemplates((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (hasValidationErrors) {
      notify(translate('messageTemplatesPage.validationError'), { type: "warning" });
      return;
    }

    setSaving(true);
    try {
      // Save all templates as a single document to sync/messageTemplates
      await dataProvider.set("sync", {
        id: "messageTemplates",
        data: {
          id: "messageTemplates",
          notification: templates.notification || "",
          shareArtist: templates.shareArtist || "",
          shareRadio: templates.shareRadio || "",
          shareShow: templates.shareShow || "",
          updatedAt: new Date().toISOString(),
        },
        previousData: initialTemplates,
      });

      setInitialTemplates(templates);
      notify(translate('messageTemplatesPage.saveSuccess'), { type: "success" });
      refresh();
    } catch (error: any) {
      notify(`${translate('messageTemplatesPage.saveError')}: ${error.message}`, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setTemplates(initialTemplates);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {translate('messageTemplatesPage.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {translate('messageTemplatesPage.subtitle')}
      </Typography>

      <Paper elevation={2} sx={{ p: 3 }}>
        <TextField
          fullWidth
          label={translate('messageTemplatesPage.notificationLabel')}
          value={templates.notification || ""}
          onChange={(e) => handleChange("notification", e.target.value)}
          multiline
          rows={3}
          margin="normal"
          variant="outlined"
          error={!!validationErrors.notification}
          helperText={validationErrors.notification || translate('messageTemplatesPage.notificationHelper')}
        />

        <TextField
          fullWidth
          label={translate('messageTemplatesPage.shareArtistLabel')}
          value={templates.shareArtist || ""}
          onChange={(e) => handleChange("shareArtist", e.target.value)}
          multiline
          rows={3}
          margin="normal"
          variant="outlined"
          error={!!validationErrors.shareArtist}
          helperText={validationErrors.shareArtist || translate('messageTemplatesPage.shareArtistHelper')}
        />

        <TextField
          fullWidth
          label={translate('messageTemplatesPage.shareRadioLabel')}
          value={templates.shareRadio || ""}
          onChange={(e) => handleChange("shareRadio", e.target.value)}
          multiline
          rows={3}
          margin="normal"
          variant="outlined"
          error={!!validationErrors.shareRadio}
          helperText={validationErrors.shareRadio || translate('messageTemplatesPage.shareRadioHelper')}
        />

        <TextField
          fullWidth
          label={translate('messageTemplatesPage.shareShowLabel')}
          value={templates.shareShow || ""}
          onChange={(e) => handleChange("shareShow", e.target.value)}
          multiline
          rows={3}
          margin="normal"
          variant="outlined"
          error={!!validationErrors.shareShow}
          helperText={validationErrors.shareShow || translate('messageTemplatesPage.shareShowHelper')}
        />

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            disabled={!isDirty || saving || hasValidationErrors}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
          >
            {saving ? translate('messageTemplatesPage.saving') : translate('messageTemplatesPage.saveButton')}
          </Button>
          {isDirty && (
            <Button variant="outlined" onClick={handleReset} disabled={saving}>
              {translate('messageTemplatesPage.resetButton')}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default MessageTemplatesPage;
